import React from 'react';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import urlParse from 'url-parse';

import MidArticleEvent from '@components/MidArticle/Event';
import { MidArticleEventProps } from '@components/MidArticle/Event/interfaces';
import RelatedStoriesMidArticle from '@components/RelatedStories/MidArticle';
import { Article } from '@interfaces/homepage';
import { READER_URL_PRODUCTION } from '@src/env';

const MID_ARTICLE_EVENT_UNIT = 'mid_article_event_unit';
const MID_ARTICLE_RECIRC_UNIT = 'mid_article_recirc_unit';

const transformLinksToBeExternal = (node) => {
  if (node.type === 'tag' && node.name === 'a') {
    const href = node.attribs?.href;

    // Ignore jump and mail to links
    if (href && !(href.startsWith('#') || href.startsWith('mailto:'))) {
      const url = urlParse(href, true);

      if (url.host !== urlParse(READER_URL_PRODUCTION).host) {
        // rc query params are often included in links by mistake by authors
        // copying URLs.
        if (url.query && url.query.rc) {
          delete url.query.rc;
        }

        return {
          ...node,
          attribs: {
            ...node.attribs,
            href: url.toString(),
            target: '_blank', // Set links to open in a new tab
          },
        };
      }
    }
  }

  return node;
};

const transformers = (node) => {
  return [transformLinksToBeExternal].reduce((accNode, tf) => {
    return tf(accNode);
  }, node);
};

const transform = (node, index) => convertNodeToElement(transformers(node), index, transform);

const articleTransform = (node, index, relatedArticles, relatedEvent) => {
  if (node?.attribs?.class?.includes('embedded-timeline') && node?.attribs?.style) {
    node.attribs.style = '';
  } else if (node.name === MID_ARTICLE_EVENT_UNIT && relatedEvent) {
    return <MidArticleEvent key={`mid-article-event-${index}`} event={relatedEvent} />;
  } else if (node.name === MID_ARTICLE_RECIRC_UNIT && relatedArticles) {
    return (
      <RelatedStoriesMidArticle
        key={`related-stories-mid-article-${index}`}
        articles={relatedArticles}
      />
    );
  }
  return convertNodeToElement(transformers(node), index, articleTransform);
};

const AuthoredContentReactToHtmlParser = (text) => {
  return ReactHtmlParser(text, { transform });
};

const AuthoredArticleContentReactToHtmlParser = ({
  text,
  relatedArticles = [],
  relatedEvent,
}: {
  text: string;
  relatedArticles?: Article[];
  relatedEvent?: MidArticleEventProps;
}) => {
  return ReactHtmlParser(text, {
    transform: (node, index) => articleTransform(node, index, relatedArticles, relatedEvent),
  });
};

export { AuthoredArticleContentReactToHtmlParser, AuthoredContentReactToHtmlParser };
